import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@components/Header';
import Footer from '@components/Footer';
import { useAuth } from '@hooks/useAuth';
import { useValidateCode, useApplyCode, useRedeemHistory } from '@hooks/useRedeem';
import './Redeem.css';

function Redeem() {
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();
    const { validateCode, loading: validating } = useValidateCode();
    const { applyCode, loading: applying } = useApplyCode();
    const { history, mutate: refreshHistory } = useRedeemHistory();

    const [code, setCode] = useState('');
    const [validatedData, setValidatedData] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    if (!isLoggedIn) {
        return (
            <div className="page-container">
                <Header />
                <div className="redeem-page">
                    <div className="redeem-container">
                        <div className="redeem-card">
                            <div className="redeem-icon">🎟️</div>
                            <h2>Redeem VIP Code</h2>
                            <p className="redeem-message">
                                Please <button onClick={() => navigate('/login')} className="link-btn">login</button> to redeem VIP codes
                            </p>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    const handleValidate = async () => {
        if (!code.trim()) {
            setError('Please enter a code');
            return;
        }

        setError('');
        setValidatedData(null);

        const result = await validateCode(code.trim());

        if (result.success) {
            setValidatedData(result.data);
        } else {
            setError(result.error);
        }
    };

    const handleApply = async () => {
        if (!validatedData) {
            setError('Please validate the code first');
            return;
        }

        setError('');

        const result = await applyCode(code.trim());

        if (result.success) {
            setSuccess(true);
            setValidatedData(null);
            setCode('');
            refreshHistory();
            
            // Refresh VIP status after 2 seconds and redirect
            setTimeout(() => {
                window.location.href = '/vip';
            }, 2000);
        } else {
            setError(result.error);
        }
    };

    const handleReset = () => {
        setCode('');
        setValidatedData(null);
        setError('');
        setSuccess(false);
    };

    return (
        <div className="page-container">
            <Header />
            <div className="redeem-page">
                <div className="redeem-container">
                    {success ? (
                        <div className="redeem-card success-card">
                            <div className="success-icon">✅</div>
                            <h2>Success!</h2>
                            <p>VIP activated successfully!</p>
                            <p className="redirect-text">Redirecting to VIP page...</p>
                        </div>
                    ) : (
                        <div className="redeem-card">
                            <div className="redeem-icon">🎟️</div>
                            <h2>Redeem VIP Code</h2>
                            <p className="redeem-subtitle">Have a VIP code? Enter it below to activate your benefits</p>

                            <div className="redeem-form">
                                <div className="form-group">
                                    <label htmlFor="code">VIP Code</label>
                                    <input
                                        type="text"
                                        id="code"
                                        className="redeem-input"
                                        placeholder="Enter your code (e.g., VIP2024)"
                                        value={code}
                                        onChange={(e) => {
                                            setCode(e.target.value.toUpperCase());
                                            setValidatedData(null);
                                            setError('');
                                        }}
                                        disabled={validating || applying}
                                    />
                                </div>

                                {error && (
                                    <div className="error-message">
                                        <i className="fas fa-exclamation-circle"></i>
                                        {error}
                                    </div>
                                )}

                                {validatedData && (
                                    <div className="code-preview">
                                        <div className="preview-header">
                                            <i className="fas fa-check-circle"></i>
                                            <span>Code is valid!</span>
                                        </div>
                                        <div className="preview-details">
                                            <div className="preview-item">
                                                <span className="preview-label">Package:</span>
                                                <span className="preview-value">
                                                    {validatedData.package_info.package_name}
                                                    {validatedData.package_info.is_permanent && ' (Lifetime)'}
                                                </span>
                                            </div>
                                            {validatedData.package_info.duration_days && (
                                                <div className="preview-item">
                                                    <span className="preview-label">Duration:</span>
                                                    <span className="preview-value">
                                                        {validatedData.package_info.duration_days} days
                                                    </span>
                                                </div>
                                            )}
                                            {validatedData.description && (
                                                <div className="preview-item">
                                                    <span className="preview-label">Description:</span>
                                                    <span className="preview-value">{validatedData.description}</span>
                                                </div>
                                            )}
                                            {validatedData.expires_at && (
                                                <div className="preview-item">
                                                    <span className="preview-label">Code expires:</span>
                                                    <span className="preview-value">
                                                        {new Date(validatedData.expires_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="preview-item">
                                                <span className="preview-label">Uses remaining:</span>
                                                <span className="preview-value">
                                                    {validatedData.uses_remaining}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="redeem-actions">
                                    {!validatedData ? (
                                        <button
                                            className="redeem-btn validate-btn"
                                            onClick={handleValidate}
                                            disabled={!code.trim() || validating}
                                        >
                                            {validating ? (
                                                <><i className="fas fa-spinner fa-spin"></i> Validating...</>
                                            ) : (
                                                <><i className="fas fa-search"></i> Validate Code</>
                                            )}
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                className="redeem-btn apply-btn"
                                                onClick={handleApply}
                                                disabled={applying}
                                            >
                                                {applying ? (
                                                    <><i className="fas fa-spinner fa-spin"></i> Applying...</>
                                                ) : (
                                                    <><i className="fas fa-gift"></i> Redeem Now</>
                                                )}
                                            </button>
                                            <button
                                                className="redeem-btn cancel-btn"
                                                onClick={handleReset}
                                                disabled={applying}
                                            >
                                                <i className="fas fa-times"></i> Cancel
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* History Section */}
                            <div className="redeem-history-section">
                                <button
                                    className="history-toggle"
                                    onClick={() => setShowHistory(!showHistory)}
                                >
                                    <i className={`fas fa-chevron-${showHistory ? 'up' : 'down'}`}></i>
                                    View Redeem History ({history.length})
                                </button>

                                {showHistory && (
                                    <div className="redeem-history">
                                        {history.length === 0 ? (
                                            <p className="no-history">No redeem history yet</p>
                                        ) : (
                                            <div className="history-list">
                                                {history.map((item) => (
                                                    <div key={item.id} className={`history-item ${item.success ? 'success' : 'failed'}`}>
                                                        <div className="history-header">
                                                            <span className="history-code">
                                                                {item.success ? '✅' : '❌'} {item.code}
                                                            </span>
                                                            <span className="history-date">
                                                                {new Date(item.redeemed_at).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                        {item.success ? (
                                                            <p className="history-desc">
                                                                {item.redeem_codes?.description || `Package: ${item.package_code}`}
                                                            </p>
                                                        ) : (
                                                            <p className="history-error">{item.error_reason}</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="redeem-info">
                        <h3>How to use:</h3>
                        <ol>
                            <li>Enter your VIP code in the input field above</li>
                            <li>Click "Validate Code" to check if it's valid</li>
                            <li>Review the package details</li>
                            <li>Click "Redeem Now" to activate your VIP</li>
                        </ol>
                        <p className="info-note">
                            <i className="fas fa-info-circle"></i>
                            Don't have a code? Contact support on <a href="https://t.me/DinoBytes" target="_blank" rel="noopener noreferrer">Telegram</a>
                        </p>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default Redeem;

